import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    useTheme,
    IconButton,
    Collapse,
    tableFooterClasses,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { PanelBlock, PanelRow, PanelColumn } from "../../types/interfaces";
import { TableField } from "../../types/interfaces";
import { useFormContext } from "./FormContext";
import ControlRenderer from "./ControlRenderer";

interface Props {
    panel: PanelBlock;
    allFields: TableField[];
    parentPath?: string;
    depth?: number;
    recordData?: Record<string, any>; 
}

/**
 * Renders a panel block with dynamic rows and columns.
 * Fully theme-aware, responsive, accessible, and recursively nests children.
 */
const PanelRenderer: React.FC<Props> = ({ panel, allFields, parentPath = "", depth = 0, recordData }) => {
    const { visible } = useFormContext();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const panelId = panel.id;
    const fullPath = parentPath ? `${parentPath}.${panelId}` : panelId;
    const isVisible = visible[panelId] ?? true;

    const [expanded, setExpanded] = useState(true);

    // Optionally collapse nested panels by default
    useEffect(() => {
        if (depth > 2) setExpanded(false);
    }, [depth]);

    if (!isVisible) return null;

    return (
        <Box
            className={`form-panel depth-${depth} ${panel.cssClass ?? ""}`}
            data-panel-id={fullPath}
            sx={{
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                padding: 2,
                mb: 3,
                px: { xs: 1, sm: 3 }, // 1 = 8px, 3 = 24px
                backgroundColor: isDark ? theme.palette.background.default : "#fdfdfd",
                transition: "all 0.3s ease-in-out",
                boxShadow: depth > 0 ? `inset 0 0 4px rgba(0,0,0,0.1)` : undefined,
            }}
            role="region"
            aria-labelledby={`${fullPath}-header`}
        >
            {/* Panel Header */}
            {panel.name && (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                    className="form-panel-header"
                >
                    <Typography
                        id={`${fullPath}-header`}
                        variant="h6"
                        fontWeight={600}
                        component="h2"
                    >
                        {panel.name}
                    </Typography>

                    {/* Expand/Collapse Button */}
                    <IconButton
                        size="small"
                        aria-label={expanded ? "Collapse panel" : "Expand panel"}
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            )}

            {/* Optional Help Text */}
            {panel.helpText && (
                <Typography
                    variant="body2"
                    sx={{ mb: 2, fontStyle: "italic", color: theme.palette.text.secondary }}
                >
                    {panel.helpText}
                </Typography>
            )}

            {/* Panel Body */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                {panel.rows.map((row: PanelRow, rowIndex: number) => (
                    <Box
                        key={`${fullPath}.row.${rowIndex}`}
                        className="form-panel-row"
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 2,
                            justifyContent: "space-between",
                            px: { xs: 1, sm: 3 }, // 1 = 8px, 3 = 24px
                        }}
                    >
                        {row.columns.map((col: PanelColumn, colIndex) => {
                            const content = col.content;
                            const colKey = `${fullPath}.col.${rowIndex}.${colIndex}`;
                            const widthPercent = `${95 / (panel.columns || 1)}%`;

                            if (!content) {
                                return (
                                    <Box
                                        key={colKey}
                                        className="form-panel-column empty"
                                        sx={{
                                            flex: 1,
                                            minWidth: { xs: "100%", sm: "47%" },
                                            maxWidth: "100%",
                                            flexBasis: { xs: "100%", sm: "47%" },
                                            px: { xs: 1, sm: 3 }, // 1 = 8px, 3 = 24px
                                          }}
                                    />
                                );
                            }

                            // Nested Panel
                            if ("rows" in content) {
                                return (
                                    <Box
                                        key={colKey}
                                        className="form-panel-column nested-panel"
                                        sx={{
                                            flex: 1,
                                            minWidth: { xs: "100%", sm: "47%" },
                                            maxWidth: "100%",
                                            flexBasis: { xs: "100%", sm: "47%" },
                                            px: { xs: 1, sm: 3 }, // 1 = 8px, 3 = 24px
                                          }}
                                    >
                                        <PanelRenderer
                                            panel={content}
                                            allFields={allFields}
                                            parentPath={fullPath}
                                            depth={depth + 1}
                                            recordData={recordData}
                                        />
                                    </Box>
                                );
                            }

                            // Regular Control or Field
                            const fieldId = (content as any).fieldID ?? (content as any).id;
                            const fieldMeta = allFields.find(f => f.fieldID === fieldId);

                            return (
                                <Box
                                    key={colKey}
                                    className="form-panel-column"
                                    sx={{
                                        flex: 1,
                                        minWidth: { xs: "100%", sm: "47%" },
                                        maxWidth: "100%",
                                        flexBasis: { xs: "100%", sm: "47%" },
                                      }}
                                >
                                    <ControlRenderer
                                        field={content as any}
                                        fieldMeta={fieldMeta}                         
                                        recordData={recordData}
                                        tableFields={allFields}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Collapse>
        </Box>
    );
};

export default PanelRenderer;
